import { Vec3 } from '../core/vec';

/** Aktuelle Auswahl (Kanten + Flächen), getrennt vom Modell — wie SketchUps Auswahl. */
export interface Selection {
  edges: Set<number>;
  faces: Set<number>;
}

export class SelectionState {
  edges = new Set<number>();
  faces = new Set<number>();
  private listeners = new Set<() => void>();

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  get isEmpty(): boolean {
    return this.edges.size === 0 && this.faces.size === 0;
  }

  clear(): void {
    if (this.isEmpty) return;
    this.edges.clear();
    this.faces.clear();
    this.emit();
  }

  set(edgeIds: Iterable<number>, faceIds: Iterable<number>): void {
    this.edges = new Set(edgeIds);
    this.faces = new Set(faceIds);
    this.emit();
  }

  toggleEdge(id: number): void {
    if (this.edges.has(id)) this.edges.delete(id);
    else this.edges.add(id);
    this.emit();
  }

  toggleFace(id: number): void {
    if (this.faces.has(id)) this.faces.delete(id);
    else this.faces.add(id);
    this.emit();
  }

  addFace(id: number): void {
    this.faces.add(id);
    this.emit();
  }

  addEdge(id: number): void {
    this.edges.add(id);
    this.emit();
  }

  snapshot(): Selection {
    return { edges: new Set(this.edges), faces: new Set(this.faces) };
  }

  restore(sel: Selection): void {
    this.edges = new Set(sel.edges);
    this.faces = new Set(sel.faces);
    this.emit();
  }

  /** Vertices der aktuellen Auswahl. */
  vertexIds(model: { verticesOf(edgeIds: Iterable<number>, faceIds: Iterable<number>): Set<number> }): Set<number> {
    return model.verticesOf(this.edges, this.faces);
  }
}

/** Aktive Markierung für Vorschau (Hover-Flächen bei Push/Pull etc.). */
export interface HoverHighlight {
  faceId?: number;
  edgeId?: number;
  point?: Vec3;
  snapKind?: string;
}
